import React, { useEffect, useState } from 'react';
import { Form, Modal, Row } from 'react-bootstrap';
import { toast } from '../../toasts';
import moment from 'moment';
import GoogleCalendarService from '../../services/googleCalendar.service';
import { getError } from '../../utils';
import SpinnerOverlay from '../SpinnerOverlay/SpinnerOverlay';
import { Button } from '../EntityList';

// Google all-day events use `start.date`/`end.date` (no time); timed events use
// `start.dateTime`/`end.dateTime` (with offset). Either way, prefill the
// datetime-local input with a local-format string moment can parse back.
const toDateTimeLocalValue = (dateOrDateTime) => {
	if (!dateOrDateTime) return '';
	return moment(dateOrDateTime.dateTime || dateOrDateTime.date).format('YYYY-MM-DDTHH:mm');
};

/**
 * Modal for creating or editing a native Google Calendar event inline.
 * Props:
 *   show: boolean
 *   onHide: () => void
 *   calendarId: string — the currently selected Google calendar
 *   editingEvent: the raw Google event to edit, or null/undefined to create a new one
 *   defaultDate: when creating (no editingEvent), pre-fills start/end to this day at 09:00–10:00
 *   onSaved: () => void — called after a successful create/update, so the parent can re-list events
 */
const GoogleEventModal = ({ show, onHide, calendarId, editingEvent, defaultDate, onSaved }) => {
	const [summary, setSummary] = useState('');
	const [start, setStart] = useState('');
	const [end, setEnd] = useState('');
	const [description, setDescription] = useState('');
	const [saving, setSaving] = useState(false);

	const isEditing = !!editingEvent;

	const reset = () => {
		setSummary('');
		setStart('');
		setEnd('');
		setDescription('');
	};

	useEffect(() => {
		if (!show) return;
		if (editingEvent) {
			setSummary(editingEvent.summary || '');
			setStart(toDateTimeLocalValue(editingEvent.start));
			setEnd(toDateTimeLocalValue(editingEvent.end));
			setDescription(editingEvent.description || '');
		} else if (defaultDate) {
			reset();
			setStart(moment.utc(defaultDate).format('YYYY-MM-DDT09:00'));
			setEnd(moment.utc(defaultDate).format('YYYY-MM-DDT10:00'));
		} else {
			reset();
		}
	}, [show, editingEvent, defaultDate]);

	const handleSave = async () => {
		if (!summary) { toast.error('Debe ingresar un título.'); return; }
		if (!start) { toast.error('Debe ingresar la fecha/hora de inicio.'); return; }
		if (!end) { toast.error('Debe ingresar la fecha/hora de fin.'); return; }
		if (moment(end).isBefore(moment(start))) { toast.error('La fecha de fin debe ser posterior al inicio.'); return; }

		try {
			setSaving(true);
			const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			const payload = {
				calendarId,
				summary,
				description,
				start: { dateTime: moment(start).toISOString(), timeZone },
				end: { dateTime: moment(end).toISOString(), timeZone },
			};
			if (isEditing) {
				await GoogleCalendarService.updateEvent(editingEvent.id, payload);
				toast.success('Evento actualizado');
			} else {
				await GoogleCalendarService.createEvent(payload);
				toast.success('Evento creado en Google Calendar');
			}
			reset();
			onHide();
			onSaved();
		} catch (ex) {
			toast.error(getError(ex));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal show={show} onHide={onHide} size='lg' centered>
			<Modal.Header className='tw:border-b tw:border-border tw:bg-surface-2' closeButton={!saving}>
				<Modal.Title className='tw:text-[15px] tw:font-semibold'>{isEditing ? 'Editar Evento de Google Calendar' : 'Nuevo Evento de Google Calendar'}</Modal.Title>
			</Modal.Header>
			<Modal.Body className='tw:relative tw:bg-surface tw:px-5 tw:py-5'>
				{saving && <SpinnerOverlay />}
				<Form onSubmit={(e) => e.preventDefault()}>
					<Row className="mb-3">
						<Form.Group className="col-12 form-group required" controlId="google-event-summary">
							<Form.Label>Título</Form.Label>
							<Form.Control
								type="text"
								value={summary}
								onChange={(e) => setSummary(e.target.value)}
								placeholder="Título del evento..."
							/>
						</Form.Group>
					</Row>
					<Row className="mb-3">
						<Form.Group className="col-6 form-group required" controlId="google-event-start">
							<Form.Label>Inicio</Form.Label>
							<Form.Control
								type="datetime-local"
								value={start}
								onChange={(e) => setStart(e.target.value)}
							/>
						</Form.Group>
						<Form.Group className="col-6 form-group required" controlId="google-event-end">
							<Form.Label>Fin</Form.Label>
							<Form.Control
								type="datetime-local"
								value={end}
								onChange={(e) => setEnd(e.target.value)}
							/>
						</Form.Group>
					</Row>
					<Row className="mb-3">
						<Form.Group className="col-12" controlId="google-event-description">
							<Form.Label>Descripción</Form.Label>
							<Form.Control
								as="textarea"
								rows={3}
								value={description}
								onChange={(e) => setDescription(e.target.value)}
							/>
						</Form.Group>
					</Row>
				</Form>
			</Modal.Body>
			<Modal.Footer className='tw:flex tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2'>
				<Button onClick={onHide} disabled={saving}>Cancelar</Button>
				<Button variant='primary' onClick={handleSave} disabled={saving}>Guardar</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default GoogleEventModal;
